export class ApiError extends Error {
  constructor(message: string, public status: number, public code?: string) {
    super(message)
  }
}

function requestFailureMessage(status: number, message?: string, fallback = 'Something went wrong. Please try again.') {
  return message || (status === 502 || status === 503 || status === 504
    ? 'The service is temporarily unavailable. Your changes may not have been saved. Retry loading before saving again.'
    : fallback)
}

type ApiPayload<T> = { data: T; meta?: Record<string, unknown> }

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  })
  const payload = await response.json().catch(() => null) as (ApiPayload<T> & { error?: { message?: string; code?: string } }) | null
  if (!response.ok) {
    if (response.status === 401 && payload?.error?.code === 'AUTH_REQUIRED') {
      window.dispatchEvent(new Event('termovista:unauthorized'))
    }
    throw new ApiError(requestFailureMessage(response.status, payload?.error?.message), response.status, payload?.error?.code)
  }
  if (!payload || !('data' in payload)) throw new ApiError('The server returned an unexpected response.', 502)
  return payload.data
}

async function download(path: string, init: RequestInit = {}) {
  const response = await fetch(path, { ...init, credentials: 'same-origin' })
  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: { message?: string; code?: string } } | null
    if (response.status === 401 && payload?.error?.code === 'AUTH_REQUIRED') window.dispatchEvent(new Event('termovista:unauthorized'))
    throw new ApiError(requestFailureMessage(response.status, payload?.error?.message, 'The document could not be generated.'), response.status, payload?.error?.code)
  }
  const disposition = response.headers.get('content-disposition') || ''
  const match = /filename="([^"]+)"/.exec(disposition)
  return { blob: await response.blob(), fileName: match?.[1] || 'document.pdf' }
}

async function upload<T>(path: string, file: File): Promise<T> {
  const formData = new FormData()
  formData.set('file', file)
  const response = await fetch(path, { method: 'POST', credentials: 'same-origin', body: formData })
  const payload = await response.json().catch(() => null) as (ApiPayload<T> & { error?: { message?: string; code?: string } }) | null
  if (!response.ok) {
    if (response.status === 401 && payload?.error?.code === 'AUTH_REQUIRED') window.dispatchEvent(new Event('termovista:unauthorized'))
    throw new ApiError(requestFailureMessage(response.status, payload?.error?.message, 'The document could not be uploaded.'), response.status, payload?.error?.code)
  }
  if (!payload || !('data' in payload)) throw new ApiError('The server returned an unexpected response.', 502)
  return payload.data
}

export const api = {
  list: <T>(entity: string, query = '') => request<T[]>(`/api/${entity}${query}`),
  get: <T>(entity: string, id: string) => request<T>(`/api/${entity}/${id}`),
  create: <T>(entity: string, values: Record<string, unknown>) => request<T>(`/api/${entity}`, { method: 'POST', body: JSON.stringify(values) }),
  update: <T>(entity: string, id: string, values: Record<string, unknown>) => request<T>(`/api/${entity}/${id}`, { method: 'PATCH', body: JSON.stringify(values) }),
  remove: <T>(entity: string, id: string) => request<T>(`/api/${entity}/${id}`, { method: 'DELETE' }),
  action: <T>(entity: string, id: string, action: string, values: Record<string, unknown> = {}) =>
    request<T>(`/api/${entity}/${id}/${action}`, { method: 'POST', body: JSON.stringify(values) }),
  related: <T>(entity: string, id: string, relation: string) => request<T[]>(`/api/${entity}/${id}/${relation}`),
  relatedOne: <T>(entity: string, id: string, relation: string) => request<T>(`/api/${entity}/${id}/${relation}`),
  createRelated: <T>(entity: string, id: string, relation: string, values: Record<string, unknown>) =>
    request<T>(`/api/${entity}/${id}/${relation}`, { method: 'POST', body: JSON.stringify(values) }),
  downloadAction: (entity: string, id: string, action: string) =>
    download(`/api/${entity}/${id}/${action}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' }),
  uploadSupplierResponseDocument: <T>(quoteId: string, file: File) => upload<T>(`/api/quotes/${quoteId}/supplier-response-document`, file),
  calendar: <T>(timeMin: string, timeMax: string) =>
    request<T[]>(`/api/calendar/events?timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}`),
}
